import { useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '../api/fetchWithAuth';

const FORMATION_COLORS = {
  'Wolfcamp A':    '#378ADD',
  'Wolfcamp B':    '#1D9E75',
  'Wolfcamp':      '#378ADD',
  'Bone Spring':   '#EF9F27',
  'Spraberry':     '#D85A30',
  'Delaware Mtn':  '#7F77DD',
  'Eagle Ford':    '#D4537E',
  'Haynesville':   '#639922',
  'Barnett':       '#BA7517',
  'Austin Chalk':  '#5DCAA5',
};

function getFormationColor(name) {
  if (!name) return '#888780';
  for (const [key, color] of Object.entries(FORMATION_COLORS)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#888780';
}

const TO_RAD = Math.PI / 180;
const FT_PER_DEG_LAT = 364000;

function seededRandom(api) {
  let h = 0;
  for (let i = 0; i < (api||'').length; i++)
    h = Math.imul(31, h) + api.charCodeAt(i) | 0;
  return Math.abs(h % 1000) / 1000;
}

// Seeded schematic drift — ONLY used when a well has no RRC bottom-hole to
// draw to. Direction and magnitude are hashed off the API string (fiction),
// which is why any panel showing this must caption it "modeled drift".
function buildVerticalPathSeeded(surfLon, surfLat, tvdFt, api) {
  const seed      = seededRandom(api);
  const inclMax   = 2 + seed * 3;
  const azimuth   = seed * 360;
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
  const steps     = 30;
  const pts       = [];
  for (let i = 0; i <= steps; i++) {
    const frac    = i / steps;
    const tvd     = tvdFt * frac;
    const incl    = inclMax * Math.sin(frac * Math.PI * 0.5) * TO_RAD;
    const azi      = azimuth * TO_RAD;
    const horizFt = tvd * Math.tan(incl);
    pts.push([
      surfLon + (horizFt * Math.sin(azi)) / ftPerDegLon,
      surfLat + (horizFt * Math.cos(azi)) / FT_PER_DEG_LAT,
      -(tvd * 0.3048),
    ]);
  }
  return pts;
}

// Real-bottom vertical path — endpoint lands EXACTLY on the RRC bottom-hole.
// Azimuth and total horizontal displacement are derived from the true
// surface -> bottom-hole vector; the intermediate shape (near-vertical up top,
// drift accumulating with depth) is schematic, but the terminus is real.
function buildVerticalPathReal(surfLon, surfLat, bhLon, bhLat, tvdFt) {
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
  const dxFt = (bhLon - surfLon) * ftPerDegLon;   // east ft
  const dyFt = (bhLat - surfLat) * FT_PER_DEG_LAT; // north ft
  const totalHorizFt = Math.sqrt(dxFt * dxFt + dyFt * dyFt);
  const azi = Math.atan2(dxFt, dyFt);              // from north, east positive
  const steps = 30;
  const pts   = [];
  for (let i = 0; i <= steps; i++) {
    const frac    = i / steps;
    const tvd     = tvdFt * frac;
    const shape   = Math.sin(frac * Math.PI * 0.5); // 0 at surface, 1 at TD
    const horizFt = totalHorizFt * shape;
    pts.push([
      surfLon + (horizFt * Math.sin(azi)) / ftPerDegLon,
      surfLat + (horizFt * Math.cos(azi)) / FT_PER_DEG_LAT,
      -(tvd * 0.3048),
    ]);
  }
  return pts;
}

// Dispatcher: real RRC bottom-hole when present, seeded drift otherwise.
function buildVerticalPath(surfLon, surfLat, tvdFt, api, bhLon, bhLat) {
  return (bhLon != null && bhLat != null)
    ? buildVerticalPathReal(surfLon, surfLat, bhLon, bhLat, tvdFt)
    : buildVerticalPathSeeded(surfLon, surfLat, tvdFt, api);
}

function buildJCurve(surfLon, surfLat, heelLon, heelLat, toeLon, toeLat, tvdFt) {
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
  const dLon   = (toeLon - heelLon) * ftPerDegLon;
  const dLat   = (toeLat - heelLat) * FT_PER_DEG_LAT;
  const azDeg  = (Math.atan2(dLon, dLat) * (180 / Math.PI) + 360) % 360;
  const latFt  = Math.sqrt(dLon*dLon + dLat*dLat);
  const bur    = 6, kickoff = tvdFt * 0.6, buildFt = (90/bur)*100;
  const totalMD = kickoff + buildFt + latFt;
  const survey  = [];
  for (let i = 0; i <= 40; i++) {
    const md = totalMD*(i/40);
    let incl = 0;
    if (md > kickoff && md < kickoff+buildFt) incl = Math.min(90,((md-kickoff)/100)*bur);
    else if (md >= kickoff+buildFt) incl = 90;
    survey.push({ md, incl, azi: azDeg });
  }
  const pts = [{ x:0, y:0, z:0 }];
  for (let i = 1; i < survey.length; i++) {
    const p=survey[i-1], c=survey[i], dmd=c.md-p.md;
    const i1=p.incl*TO_RAD, i2=c.incl*TO_RAD, a1=p.azi*TO_RAD, a2=c.azi*TO_RAD;
    const arg=Math.cos(i2-i1)-Math.sin(i1)*Math.sin(i2)*(1-Math.cos(a2-a1));
    const dl=Math.acos(Math.max(-1,Math.min(1,arg)));
    const rf=dl<1e-4?1:(2/dl)*Math.tan(dl/2);
    const last=pts[pts.length-1];
    pts.push({
      x:last.x+(dmd/2)*(Math.sin(i1)*Math.sin(a1)+Math.sin(i2)*Math.sin(a2))*rf,
      y:last.y+(dmd/2)*(Math.sin(i1)*Math.cos(a1)+Math.sin(i2)*Math.cos(a2))*rf,
      z:last.z+(dmd/2)*(Math.cos(i1)+Math.cos(i2))*rf,
    });
  }
  return pts.map(p => [
    surfLon + p.x/ftPerDegLon,
    surfLat + p.y/FT_PER_DEG_LAT,
    -(p.z*0.3048),
  ]);
}

// Provenance caption for the drawn profile. The trajectory SHAPE is always
// schematic (we hold endpoints, not a deviation survey); the modifier states
// where the terminus came from so a surveyed toe is never confused with a
// modeled one, and a modeled vertical is never passed off as real.
function caveatFor(w) {
  if (!w) return '';
  if (w.wellType === 'vertical') {
    return (w.bhLon != null && w.bhLat != null)
      ? 'Schematic — bottom from RRC bottom-hole'
      : 'Schematic — modeled drift';
  }
  if (w.toeSource === 'bottomhole') return 'Schematic — toe from RRC bottom-hole';
  if (w.toeSource === 'lateral')    return 'Schematic — toe from RRC directional line';
  return 'Schematic';
}

function drawCrossSection(canvas, wellData) {
  const { surfLon, surfLat, heelLon, heelLat, toeLon, toeLat,
          tvdFt, formation, api, wellType, bhLon, bhLat } = wellData;
  if (!surfLon || !tvdFt) return;

  const dpr = window.devicePixelRatio || 1;
  const W   = canvas.offsetWidth, H = canvas.offsetHeight;
  canvas.width = W*dpr; canvas.height = H*dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const PAD = { top:24, bottom:16, left:36, right:12 };
  const plotW = W-PAD.left-PAD.right, plotH = H-PAD.top-PAD.bottom;
  ctx.clearRect(0,0,W,H);

  const isVertical = wellType === 'vertical';
  const coords = isVertical
    ? buildVerticalPath(surfLon, surfLat, tvdFt, api, bhLon, bhLat)
    : (heelLon && toeLon
        ? buildJCurve(surfLon, surfLat, heelLon, heelLat, toeLon, toeLat, tvdFt)
        : buildVerticalPath(surfLon, surfLat, tvdFt, api, bhLon, bhLat));

  // For cross-section: x = horizontal offset from surface, y = depth
  const maxHoriz = Math.max(...coords.map(c => {
    const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
    const dx = (c[0]-surfLon)*ftPerDegLon;
    const dy = (c[1]-surfLat)*FT_PER_DEG_LAT;
    return Math.sqrt(dx*dx+dy*dy);
  }), 1);
  const maxDepth = tvdFt * 1.08;

  // Equal scaling on both axes = true 90° turn
  const scZ = plotH / Math.max(maxDepth, 1);
  const scX = scZ;

  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos(surfLat * TO_RAD);
  function toCanvas(coord) {
    const dx = (coord[0]-surfLon)*ftPerDegLon;
    const dy = (coord[1]-surfLat)*FT_PER_DEG_LAT;
    const horiz = Math.sqrt(dx*dx+dy*dy);
    const depthFt = -coord[2] / 0.3048;
    return { cx: PAD.left + horiz*scX, cy: PAD.top + depthFt*scZ };
  }

  // Grid
  ctx.strokeStyle='rgba(255,255,255,0.05)'; ctx.lineWidth=0.5;
  ctx.font='9px monospace';
  for (let d=2000; d<=maxDepth; d+=2000) {
    const cy=PAD.top+d*scZ;
    ctx.beginPath(); ctx.moveTo(PAD.left,cy); ctx.lineTo(W-PAD.right,cy); ctx.stroke();
    ctx.fillStyle='rgba(255,255,255,0.55)';
    ctx.fillText((d/1000).toFixed(0)+'k', 2, cy+3);
  }

  // Surface line
  ctx.strokeStyle='rgba(255,255,255,0.15)'; ctx.lineWidth=1;
  ctx.setLineDash([3,3]);
  ctx.beginPath(); ctx.moveTo(PAD.left,PAD.top); ctx.lineTo(W-PAD.right,PAD.top); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle='rgba(255,255,255,0.7)'; ctx.font='9px sans-serif';
  ctx.fillText('Surface', PAD.left+2, PAD.top-6);

  // Formation band
  const color = getFormationColor(formation);
  if (tvdFt) {
    const bandTop = PAD.top + (tvdFt-400)*scZ;
    const bandH   = 500*scZ;
    ctx.fillStyle = color+'22';
    ctx.fillRect(PAD.left, Math.max(PAD.top,bandTop), plotW, Math.min(bandH,plotH));
    if (formation) {
      ctx.fillStyle=color+'aa'; ctx.font='9px sans-serif';
      ctx.fillText(formation, W-PAD.right-ctx.measureText(formation).width-4, Math.max(PAD.top+12,bandTop+12));
    }
  }

  // Well type label
  ctx.fillStyle='rgba(255,255,255,0.5)'; ctx.font='9px sans-serif';
  ctx.fillText(isVertical ? 'Vertical well' : 'Horizontal well', PAD.left+2, PAD.top+12);

  // Path
  // Find where the lateral section starts (where well goes horizontal)
  // coords[i][2] is negative meters below surface
  const lateralStartIdx = isVertical ? coords.length :
    coords.findIndex((c, i) => {
      if (i === 0) return false;
      const depthFt = -c[2] / 0.3048;
      return depthFt >= tvdFt * 0.92;
    });
  const effectiveLateralStart = lateralStartIdx < 1 ? Math.floor(coords.length * 0.75) : lateralStartIdx;

  // Vertical/build section
  ctx.strokeStyle='#4a5568'; ctx.lineWidth=isVertical?2:2;
  ctx.lineCap='round'; ctx.lineJoin='round';
  ctx.beginPath();
  const vertEnd = isVertical ? coords.length : Math.max(1, effectiveLateralStart+1);
  coords.slice(0, vertEnd).forEach((c,i) => {
    const p = toCanvas(c);
    i===0 ? ctx.moveTo(p.cx,p.cy) : ctx.lineTo(p.cx,p.cy);
  });
  ctx.stroke();

  // Lateral section (horizontal wells only)
  if (!isVertical) {
    ctx.strokeStyle=color; ctx.lineWidth=2.5;
    ctx.beginPath();
    coords.slice(Math.max(0,effectiveLateralStart-1)).forEach((c,i) => {
      const p=toCanvas(c);
      i===0 ? ctx.moveTo(p.cx,p.cy) : ctx.lineTo(p.cx,p.cy);
    });
    ctx.stroke();
  }

  // Bottom dot
  const last = coords[coords.length-1];
  const lp   = toCanvas(last);
  ctx.beginPath(); ctx.arc(lp.cx,lp.cy,3,0,Math.PI*2);
  ctx.fillStyle=color; ctx.fill();

  // TD label
  if (tvdFt) {
    ctx.fillStyle=color+'ff'; ctx.font='9px monospace';
    ctx.fillText(`TD ${(tvdFt/1000).toFixed(1)}k ft`, W-PAD.right-58, PAD.top+tvdFt*scZ-4);
  }

  ctx.fillStyle='rgba(255,255,255,0.55)'; ctx.font='9px sans-serif';
  ctx.fillText('← horizontal offset →', PAD.left+4, H-2);
}

function drawMapView(canvas, wellData) {
  const { surfLon, surfLat, heelLon, heelLat, toeLon, toeLat,
          tvdFt, formation, api, wellType, lateralFt, bhLon, bhLat } = wellData;
  if (!surfLon || !tvdFt) return;

  const dpr = window.devicePixelRatio || 1;
  const W   = canvas.offsetWidth;
  const H   = canvas.offsetHeight;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  // ── THIS CANVAS KEEPS SYSTEM FONTS, DELIBERATELY ─────────────────────────
  //
  // 20 ctx.font assignments below say '9px sans-serif' or '9px monospace'.
  // Canvas takes a font string, not a class, so the app's families do not reach
  // them and this diagram will not match the type around it.
  //
  // THEY STAY, for now. Most labels here are placed at HAND-TUNED PIXEL OFFSETS
  // -- `W-PAD.right-58`, `midPt.cx+5`, `nTip.cy-4` -- against the metrics of
  // whatever 9px sans the machine has. Only one of them (the formation label at
  // :217) measures its own text. Changing the face changes every advance width
  // and silently shifts the rest, in a canvas nothing renders in a test.
  //
  // Making them follow would mean switching to measureText everywhere first,
  // which is a real change to a working diagram and not a font commit's job.
  const isDark = matchMedia('(prefers-color-scheme:dark)').matches;
  ctx.fillStyle = isDark ? '#0d1117' : '#eae6dc';
  ctx.fillRect(0, 0, W, H);

  const color = getFormationColor(formation);
  const isVertical = wellType === 'vertical';
  const FPD = FT_PER_DEG_LAT;
  const ftPerDegLon = FPD * Math.cos(surfLat * TO_RAD);

  // Build 3D path points (x=east ft, y=north ft, z=depth ft down)
  let path3d = [];
  if (isVertical) {
    path3d = buildVerticalPath(surfLon, surfLat, tvdFt, api, bhLon, bhLat).map(c => ({
      x: (c[0] - surfLon) * ftPerDegLon,
      y: (c[1] - surfLat) * FPD,
      z: -c[2] / 0.3048,
    }));
  } else if (heelLon && toeLon) {
    path3d = buildJCurve(surfLon, surfLat, heelLon, heelLat, toeLon, toeLat, tvdFt).map(c => ({
      x: (c[0] - surfLon) * ftPerDegLon,
      y: (c[1] - surfLat) * FPD,
      z: -c[2] / 0.3048,
    }));
  }
  if (!path3d.length) return;

  // Isometric projection parameters
  // Looking from SE at 30° elevation
  const ISO_AZI  = 45 * TO_RAD;   // camera azimuth
  const ISO_ELEV = 28 * TO_RAD;   // camera elevation
  const cosA = Math.cos(ISO_AZI), sinA = Math.sin(ISO_AZI);
  const cosE = Math.cos(ISO_ELEV), sinE = Math.sin(ISO_ELEV);

  function project(x, y, z) {
    // Rotate around Z by azimuth, then tilt
    const rx =  x * cosA + y * sinA;
    const ry = -x * sinA + y * cosA;
    const px =  rx;
    const py =  ry * cosE - z * sinE;
    return { px, py };
  }

  // Find scale — fit all points in canvas
  const projected = path3d.map(p => project(p.x, p.y, p.z));
  const allPx = projected.map(p => p.px);
  const allPy = projected.map(p => p.py);
  const minPx = Math.min(...allPx), maxPx = Math.max(...allPx);
  const minPy = Math.min(...allPy), maxPy = Math.max(...allPy);
  const spanX = Math.max(maxPx - minPx, 1);
  const spanY = Math.max(maxPy - minPy, 1);
  const sc    = Math.min((W * 0.72) / spanX, (H * 0.72) / spanY);
  const offX  = W / 2 - ((minPx + maxPx) / 2) * sc;
  const offY  = H / 2 - ((minPy + maxPy) / 2) * sc;

  function toCanvas(p) {
    const { px, py } = project(p.x, p.y, p.z);
    return { cx: offX + px * sc, cy: offY + py * sc };
  }

  // Draw surface grid plane. For verticals the plane sizes to the real
  // bottom-hole offset (min 500ft) so a leaning drift stays framed.
  const bhOffsetFt = (isVertical && bhLon != null && bhLat != null)
    ? Math.sqrt(
        Math.pow((bhLon - surfLon) * ftPerDegLon, 2) +
        Math.pow((bhLat - surfLat) * FPD, 2)
      )
    : 0;
  const gridFt = isVertical
    ? Math.max(500, bhOffsetFt * 1.3)
    : Math.max(lateralFt || 5000, tvdFt) * 0.6;
  const steps  = 4;
  ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  ctx.lineWidth   = 0.5;
  for (let i = 0; i <= steps; i++) {
    const t  = (i / steps - 0.5) * gridFt * 2;
    const p1 = toCanvas({ x: t,      y: -gridFt, z: 0 });
    const p2 = toCanvas({ x: t,      y:  gridFt, z: 0 });
    const p3 = toCanvas({ x: -gridFt, y: t,      z: 0 });
    const p4 = toCanvas({ x:  gridFt, y: t,      z: 0 });
    ctx.beginPath(); ctx.moveTo(p1.cx,p1.cy); ctx.lineTo(p2.cx,p2.cy); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p3.cx,p3.cy); ctx.lineTo(p4.cx,p4.cy); ctx.stroke();
  }

  // Formation plane at target depth
  if (tvdFt) {
    ctx.fillStyle = color + '18';
    const corners = [
      toCanvas({ x:-gridFt, y:-gridFt, z:tvdFt }),
      toCanvas({ x: gridFt, y:-gridFt, z:tvdFt }),
      toCanvas({ x: gridFt, y: gridFt, z:tvdFt }),
      toCanvas({ x:-gridFt, y: gridFt, z:tvdFt }),
    ];
    ctx.beginPath();
    ctx.moveTo(corners[0].cx, corners[0].cy);
    corners.slice(1).forEach(c => ctx.lineTo(c.cx, c.cy));
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = color + '40'; ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // Vertical dashed guide lines from surface to path ends
  const surfPt   = toCanvas({ x:0, y:0, z:0 });
  const bottomPt = toCanvas(path3d[path3d.length-1]);
  ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  ctx.lineWidth   = 0.5; ctx.setLineDash([2,3]);
  ctx.beginPath(); ctx.moveTo(surfPt.cx,surfPt.cy); ctx.lineTo(bottomPt.cx,bottomPt.cy);
  ctx.stroke(); ctx.setLineDash([]);

  // Find lateral start for color split
  const latStart = isVertical ? path3d.length :
    path3d.findIndex((p,i) => i>0 && p.z >= tvdFt*0.92);
  const splitAt = latStart < 1 ? Math.floor(path3d.length*0.75) : latStart;

  // Vertical/build section
  ctx.strokeStyle = isDark ? '#4a5568' : '#94a3b8';
  ctx.lineWidth   = 2; ctx.lineCap='round'; ctx.lineJoin='round';
  ctx.beginPath();
  path3d.slice(0, splitAt+1).forEach((p,i) => {
    const c = toCanvas(p);
    i===0 ? ctx.moveTo(c.cx,c.cy) : ctx.lineTo(c.cx,c.cy);
  });
  ctx.stroke();

  // Lateral section
  if (!isVertical) {
    ctx.strokeStyle = color; ctx.lineWidth = 2.5;
    ctx.beginPath();
    path3d.slice(Math.max(0,splitAt-1)).forEach((p,i) => {
      const c = toCanvas(p);
      i===0 ? ctx.moveTo(c.cx,c.cy) : ctx.lineTo(c.cx,c.cy);
    });
    ctx.stroke();
  }

  // Surface pad dot
  ctx.beginPath(); ctx.arc(surfPt.cx, surfPt.cy, 4, 0, Math.PI*2);
  ctx.fillStyle = color; ctx.fill();
  ctx.strokeStyle = isDark?'#fff':'#333'; ctx.lineWidth=0.5; ctx.stroke();

  // Toe dot
  ctx.beginPath(); ctx.arc(bottomPt.cx, bottomPt.cy, 3, 0, Math.PI*2);
  ctx.fillStyle = color; ctx.fill();

  // Labels
  ctx.fillStyle = isDark?'rgba(255,255,255,0.7)':'rgba(0,0,0,0.6)';
  ctx.font      = '9px sans-serif';
  ctx.fillText('Surface', surfPt.cx+6, surfPt.cy-4);

  if (formation && tvdFt) {
    const fPt = toCanvas({ x: gridFt*0.5, y: -gridFt*0.5, z: tvdFt });
    ctx.fillStyle = color+'cc'; ctx.font='9px sans-serif';
    ctx.fillText(formation, fPt.cx, fPt.cy-4);
  }

  // Depth label on the guide line midpoint
  const midPt = toCanvas({ x:0, y:0, z:tvdFt/2 });
  ctx.fillStyle = isDark?'rgba(255,255,255,0.3)':'rgba(0,0,0,0.3)';
  ctx.font      = '9px monospace';
  ctx.fillText((tvdFt/1000).toFixed(1)+'k ft', midPt.cx+5, midPt.cy);

  // Axis labels — N arrow
  const nBase = toCanvas({ x:0, y:-gridFt*0.7, z:0 });
  const nTip  = toCanvas({ x:0, y:-gridFt,      z:0 });
  ctx.strokeStyle = isDark?'rgba(255,255,255,0.35)':'rgba(0,0,0,0.3)';
  ctx.lineWidth=1;
  ctx.beginPath(); ctx.moveTo(nBase.cx,nBase.cy); ctx.lineTo(nTip.cx,nTip.cy); ctx.stroke();
  ctx.fillStyle = isDark?'rgba(255,255,255,0.5)':'rgba(0,0,0,0.45)';
  ctx.font='bold 9px sans-serif';
  ctx.fillText('N', nTip.cx-3, nTip.cy-4);
}

const PAD_LEFT = 8;

export default function WellDirectionalSection({ data, properties, tvdFt: tvdFtProp, formation: formationProp }) {
  const sideRef   = useRef(null);
  const mapRef    = useRef(null);
  const [wellData, setWellData] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const api = data?.source_id ?? properties?.api ?? properties?.source_id;

  useEffect(() => {
    if (!api) return;
    setLoading(true); setError(null);
    fetchWithAuth(`/api/v1/well-directionals/${api}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(d => {
        setWellData({
          ...d,
          tvdFt:     d.tvdFt     ?? tvdFtProp  ?? null,
          formation: d.formation ?? formationProp ?? null,
        });
        setLoading(false);
      })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [api]);

  function redraw() {
    if (!wellData) return;
    if (sideRef.current) drawCrossSection(sideRef.current, wellData);
    if (mapRef.current)  drawMapView(mapRef.current, wellData);
  }

  useEffect(() => { redraw(); }, [wellData]);

  useEffect(() => {
    if (!sideRef.current || !wellData) return;
    const ro = new ResizeObserver(redraw);
    ro.observe(sideRef.current);
    return () => ro.disconnect();
  }, [wellData]);

  if (!api) return null;

  const isVertical = wellData?.wellType === 'vertical';
  const driftFt = (isVertical && wellData?.bhDistM != null)
    ? Math.round(wellData.bhDistM * 3.28084)
    : null;

  return (
    <div className="border-t border-slate-800">
      <div className="flex items-center justify-between px-4 py-2.5">
        <span className="text-[10px] font-semibold tracking-widest font-mono uppercase text-slate-300">
          Directional profile
        </span>
        {wellData?.tvdFt && (
          <span className="text-[9px] text-slate-500 lowercase tracking-normal">
            {caveatFor(wellData)}
          </span>
        )}
      </div>

      <div className="px-4 pb-4">
        {loading && (
          <div className="h-[140px] flex items-center justify-center">
            <div className="w-4 h-4 border border-slate-600 border-t-slate-300 rounded-full animate-spin" />
          </div>
        )}
        {error && (
          <div className="h-[60px] flex items-center justify-center text-[10px] text-red-400">
            Could not load directional data
          </div>
        )}
        {!loading && !error && wellData && !wellData.tvdFt && (
          <div className="h-[60px] flex items-center justify-center text-[10px] text-slate-400">
            No depth data available
          </div>
        )}

        {wellData?.tvdFt && (
          <>
            {/* Two-panel layout: cross-section + map */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
              <div>
                <div className="text-[9px] text-slate-400 mb-1">Cross section</div>
                <canvas
                  ref={sideRef}
                  style={{ width:'100%', height:150, display:'block', borderRadius:6 }}
                  className="bg-[#080c10]"
                />
              </div>
              <div>
                <div className="text-[9px] text-slate-400 mb-1">Map view</div>
                <canvas
                  ref={mapRef}
                  style={{ width:'100%', height:150, display:'block', borderRadius:6 }}
                />
              </div>
            </div>

            {/* Stats row */}
            <div className="flex justify-between text-[9px] font-mono text-slate-400">
              <span>
                {isVertical ? 'Vertical' : 'Horizontal'}
              </span>
              <span>TD {wellData.tvdFt ? (wellData.tvdFt/1000).toFixed(1)+'k ft' : '—'}</span>
              <span>
                {wellData.lateralFt
                  ? 'Lat '+(wellData.lateralFt/1000).toFixed(1)+'k ft'
                  : (driftFt != null ? 'Drift '+driftFt+' ft' : '')}
              </span>
              <span>{wellData.formation ?? ''}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
